import { useCallback, useMemo } from 'react';
import { ContextMenuItem, LevelTableHeaderProps, Table, useOnScrollDown } from 'redesign';
import { InfiniteQueryObserverSuccessResult } from 'react-query';
import { useSiteDetection } from 'shared/hooks';
import { RenderUnsettledUI } from 'shared/components';
import { PaymentInfiniteResponse } from 'api/services/finance/payments.schema';
import { DateTime } from 'luxon';
import { formatPaymentStatus } from 'shared/utils/formatting';
import { useTranslation } from 'react-i18next';
import { AuthenticatedElement } from 'hq/components/common/authenticated-element';
import { HqRole } from '@types';
import { PolicyGroups } from 'shared/utils/policy-controller';
import { useGetPayments } from './hooks/use-get-payments';
import {
  getPaymentsData,
  PaymentOrderBy,
  PaymentsColumnDefinition,
  PaymentsParsedData,
  PaymentsTableOptions,
  paymentStates,
} from './payments.types';
import { getPaymentColumnDefinition } from './payment-column-definition';
import { useGetPaymentActionHandlers } from './hooks/use-get-payment-actions';
import { PaymentsFilters } from './hooks/use-payments-filters.types';
import { usePaymentsPage } from './hooks/use-payments-filters';
import { PaymentsFiltersDataProvider } from './filters/payments-filters-data-provider';

type TableProps = {
  infiniteQueryResult: InfiniteQueryObserverSuccessResult<PaymentInfiniteResponse, unknown>;
};

type Props = {
  appliedFilters: PaymentsFilters;
  setFilters: ReturnType<typeof usePaymentsPage>['setFilters'];
  areFiltersOpen: boolean;
  toggleFilters: () => void;
};

export const PaymentsTableDataProvider = (props: Props) => {
  const { site } = useSiteDetection();
  const infiniteQueryResult = useGetPayments(site, props.appliedFilters);

  if (infiniteQueryResult.status !== 'success') {
    return <RenderUnsettledUI data={infiniteQueryResult} />;
  }
  return <PaymentsTable infiniteQueryResult={infiniteQueryResult} {...props} />;
};

export const PaymentsTable = (props: Props & TableProps) => {
  const { t } = useTranslation('hq-screen-specific');
  const { infiniteQueryResult, appliedFilters, setFilters } = props;
  const flatData = useMemo(
    () => infiniteQueryResult.data.pages.flatMap((page) => page.items),
    [infiniteQueryResult.data]
  );
  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: infiniteQueryResult,
    flatData: flatData,
  });

  const { handleBlockPayment, handleUnblockPayment, handleForcePayPayment } =
    useGetPaymentActionHandlers();

  const getMenuActionsFromRow = useCallback((paymentId: string) => {
    return [
      <ContextMenuItem key={`block-${paymentId}`} onClick={() => handleBlockPayment(paymentId)}>
        Blokkeren
      </ContextMenuItem>,
      <ContextMenuItem key={`unblock-${paymentId}`} onClick={() => handleUnblockPayment(paymentId)}>
        Deblokkeren
      </ContextMenuItem>,
      <AuthenticatedElement
        key={`force-${paymentId}`}
        roles={HqRole.DEVELOPER}
        policyGroup={PolicyGroups.HQ}
      >
        <ContextMenuItem onClick={() => handleForcePayPayment(paymentId)}>Forceer</ContextMenuItem>
      </AuthenticatedElement>,
    ];
  }, []);

  const handleSortClick = (
    cell: LevelTableHeaderProps<PaymentsColumnDefinition, PaymentsTableOptions>
  ) => {
    let newOrder: 'asc' | 'desc' = appliedFilters.order === 'desc' ? 'asc' : 'desc';

    if (appliedFilters.orderBy !== cell.column.id) {
      newOrder = 'desc';
    }

    if (Object.values(PaymentOrderBy).includes(cell.column.id as PaymentOrderBy)) {
      setFilters({
        ...appliedFilters,
        orderBy: cell.column.id as PaymentOrderBy,
        order: newOrder,
      });
    }
  };

  const columns = useMemo(() => getPaymentColumnDefinition(t), []);
  const tableData = useMemo(() => getParsedTableData(flatData), [infiniteQueryResult.data]);

  return (
    <>
      <Table<PaymentsColumnDefinition, PaymentsTableOptions>
        stickyHeader={true}
        columns={columns}
        data={tableData}
        getRowActions={getMenuActionsFromRow}
        tableStyle={{ tableLayout: 'fixed' }}
        onScroll={fetchIfBottomScrolled}
        sortState={appliedFilters.order}
        orderBy={appliedFilters.orderBy}
        onSortClick={handleSortClick}
      ></Table>
      <PaymentsFiltersDataProvider
        toggleFilters={props.toggleFilters}
        appliedFilters={props.appliedFilters}
        isOpen={props.areFiltersOpen}
        handleFiltersChange={props.setFilters}
      />
    </>
  );
};
const getParsedTableData = (items: getPaymentsData[]): PaymentsParsedData[] => {
  const parsedData = items.map((item) => {
    const generatedInvoiceObj =
      !!item.reference && !!item.invoiceUrl
        ? { invoice: { invoiceUrl: item.invoiceUrl, invoiceReference: item.reference } }
        : {};
    return {
      workerName: item.workerName,
      paymentId: item.id,
      creationDate: item.creationDate ? DateTime.fromISO(item.creationDate) : undefined,
      status: formatPaymentStatus(item.status as paymentStates),
      dueDate: item.dueDate ? DateTime.fromISO(item.dueDate) : undefined,
      actions: { paymentId: item.id },
      failureReason: item.failureReason,
      ...generatedInvoiceObj,
    };
  });
  return parsedData;
};
